import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type NotificationKind = "team_invite" | "note_shared" | "comment";

export type Notification = {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  href: string | null;
  entityId: string;
  payload: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
  /** An invitation still waiting on an answer — it keeps Accept / Decline. */
  pending: boolean;
  actorName: string | null;
  actorAvatar: string | null;
};

export const notificationKeys = { all: ["notifications"] as const };

export function useNotifications() {
  return useQuery({
    queryKey: notificationKeys.all,
    queryFn: () => api<{ notifications: Notification[]; unread: number }>("/v1/notifications"),
    refetchInterval: 20_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/notifications/${id}/read`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("/v1/notifications/read-all", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

/** Where accepting lands you — a team page, or the note you just took on. */
export type AcceptResult = {
  href: string;
  teamId?: string;
  teamName?: string | null;
  noteId?: string;
  noteTitle?: string | null;
};

/**
 * Says yes to a team invite or a shared note.
 *
 * Both lists are invalidated regardless of kind: accepting is exactly the moment
 * a team or a shared note starts belonging in them, and one extra refetch is
 * cheaper than a picker that has to be reopened before it shows the new entry.
 */
export function useAcceptInviteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<AcceptResult>(`/v1/notifications/${id}/accept`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
      qc.invalidateQueries({ queryKey: ["teams"] });
      qc.invalidateQueries({ queryKey: ["notes"] });
    },
  });
}

export function useDeclineInviteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/notifications/${id}/decline`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
      qc.invalidateQueries({ queryKey: ["notes"] });
    },
  });
}
